import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import { computeLineFinancials, getMoneyPrecision } from '../purchaseOrder/poItemGridUtils';
import './pendingRequisitionsGrid.css';

export const getRequisitionTypeApiValue = (requisitionType) => {
  const label = (requisitionType?.label ?? requisitionType?.value ?? requisitionType ?? '').toString().trim();
  if (!label || label.toLowerCase() === 'none') return 'Inventory';
  return label;
};

const numberFormatter = (params) => {
  const val = params.value;
  if (val === null || val === undefined || val === '') return '';
  const num = Number(val);
  return Number.isNaN(num) ? val : num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const PendingRequisitionsGrid = ({ onSelectionChange, requisitionType, selectedCheck, currencyPrecision, setSelectedRows, selectedRows,
  setOverallDiscount, grandTotal, setVatAmount, vatAmount,
  overallDiscount, setDiscountAmount, discountAmount, setGrandTotal }) => {
  const gridRef = useRef(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const moneyPrecision = getMoneyPrecision(currencyPrecision);

  const typeKey = useMemo(() => getRequisitionTypeApiValue(requisitionType), [requisitionType]);
  // const calculatedTotal = selectedRows.reduce((sum, row) => sum + (parseFloat((row?.balQty * row?.unitPrice) - row?.vatAmount) || 0), 0);
  const calculatedTotal = selectedRows.reduce((sum, row) => {
    const amount = parseFloat((row?.balQty * row?.unitPrice)) || 0;
    const vatAmount = parseFloat(row?.vatAmount) || 0;
    let final = selectedCheck === true ? amount : (amount - vatAmount)
    return sum + (final);
  }, 0);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    const total = selectedRows?.reduce(
      (sum, row) => sum + Number((row?.balQty || 0) * (row?.unitPrice || 0)),
      0
    );

    const vat = Number(selectedRows?.[0]?.vatPerc || 0);

    let taxableAmount = total;

    // VAT Included
    if (!selectedCheck) {
      taxableAmount = total / (1 + vat / 100);
    }

    const discountAmt = (taxableAmount * overallDiscount) / 100;
    const afterDiscount = taxableAmount - discountAmt;
    const vatAmt = (afterDiscount * vat) / 100;

    const grandTotal = afterDiscount + vatAmt;
    debugger
    setDiscountAmount(discountAmt);
    setVatAmount(vatAmt);
    setGrandTotal(grandTotal);
  }, [selectedRows, overallDiscount, selectedCheck]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        width: 48,
        pinned: 'left',
      },
      { headerName: 'Requisition No', field: 'requisitionNo', suppressMovable: true, width: 140, filter: true },
      { headerName: 'Req_Sr No', field: 'srno', suppressMovable: true, width: 80 },
      { headerName: 'Item Code', field: 'itemCode', suppressMovable: true, width: 120 },
      { headerName: 'Description', field: 'itemDescription', suppressMovable: true, width: 140 },
      { headerName: 'Specification', field: 'specification', suppressMovable: true, flex: 1, minWidth: 180 },
      { headerName: 'UOM', field: 'baseUOM', suppressMovable: true, width: 80 },
      { headerName: 'Qty', field: 'balQty', suppressMovable: true, width: 90, valueFormatter: numberFormatter },
      { headerName: 'Unit Price', field: 'unitPrice', suppressMovable: true, width: 100, valueFormatter: numberFormatter },
      { headerName: 'VAT', field: 'vatDesc', suppressMovable: true, width: 140 },
      { headerName: 'VAT %', field: 'vatPerc', suppressMovable: true, width: 90, valueFormatter: numberFormatter },
      { headerName: 'VAT Amt', field: 'vatAmount', suppressMovable: true, width: 90, valueFormatter: numberFormatter },
      { headerName: 'Delivery Date', field: 'deliveryDate', suppressMovable: true, width: 120 },
      { headerName: 'Cost Center 1', field: 'costCenter1', suppressMovable: true, width: 150 },
      { headerName: 'Cost Center 2', field: 'costCenter2', suppressMovable: true, width: 120 },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const getRowId = useCallback((params) => `${params.data?.requisitionNo ?? ''}-${params.data?.srno ?? ''}-${params.data?.itemCode ?? ''}`, []);


  const fetchPending = useCallback(async () => {
    setLoading(true);
    setSelectedRows([]);
    gridRef.current?.api?.deselectAll();
    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();
      params.append('Type', typeKey);
      const response = await apiService.getApiParamater('GET_PENDING_REQUISITIONS_DPK', `?${params.toString()}`);
      const list = response?.data?.result ?? [];
      const normalized = Array.isArray(list) ? list : [];
      setRows(normalized);
      if (normalized.length === 0) {
        toast.info('No pending requisitions found');
      }
    } catch (error) {
      console.error('Error fetching pending requisitions:', error);
      toast.error('Failed to load pending requisitions');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [typeKey]);

  // Load once on mount; re-fetch only when Requisition Type changes (not on every parent re-render)
  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only refetch when typeKey changes
  }, [typeKey]);

  const onSelectionChanged = useCallback((event) => {
    const selection = event.api.getSelectedRows();
    setSelectedRows(selection);
    onSelectionChangeRef.current?.(selection);
  }, []);

  const handleSelectAll = () => {
    gridRef.current?.api?.selectAll();
  };

  const handleDeselectAll = () => {
    gridRef.current?.api?.deselectAll();
    setSelectedRows([]);
    onSelectionChangeRef.current?.([]);
  };

  const handleRefresh = () => {
    onSelectionChangeRef.current?.([]);
    fetchPending();
  };
  const handleOverallDiscount = (e) => {
    const percentage = Number(e.target.value) || 0;

    if (percentage > 100) {
      toast.warning("Discount percentage cannot exceed 100%");
      return;
    }

    const total = selectedRows.reduce(
      (sum, row) => sum + Number((row?.balQty || 0) * (row?.unitPrice || 0)),
      0
    );

    const vat = Number(selectedRows?.[0]?.vatPerc || 0);

    let taxableAmount = total;
    let vatAmt = 0;

    // VAT Included
    if (!selectedCheck) {
      taxableAmount = total / (1 + vat / 100);
    }

    const discountAmt = (taxableAmount * percentage) / 100;

    if (discountAmt > taxableAmount) {
      toast.warning("Discount amount cannot exceed total amount");
      return;
    }

    const afterDiscount = taxableAmount - discountAmt;

    // Calculate VAT after discount
    vatAmt = (afterDiscount * vat) / 100;

    const grandTotal = selectedCheck
      ? afterDiscount + vatAmt // VAT Excluded
      : afterDiscount + vatAmt; // VAT Included (reconstructed total)

    setOverallDiscount(percentage);
    setDiscountAmount(discountAmt);
    setVatAmount(vatAmt);
    setGrandTotal(grandTotal);
  };
  const onRowSelected = useCallback((params) => {
    const { node, api, data } = params;

    // Prevent infinite loop
    if (node.__processing) return;
    debugger
    node.__processing = true;

    api.forEachNode((rowNode) => {
      if (
        rowNode !== node &&
        rowNode.data.requisitionNo === data.requisitionNo &&
        rowNode.isSelected() !== node.isSelected()
      ) {
        rowNode.__processing = true;
        rowNode.setSelected(node.isSelected());
        rowNode.__processing = false;
      }
    });

    node.__processing = false;

    // Update selected rows state
    const selected = api.getSelectedRows();
    setSelectedRows(selected);
    onSelectionChangeRef.current?.(selected);
  }, []);
  return (
    <div className="pending-req-grid-section mb-4">
      <div className="row mb-3">
        <div className="col-md-4">
          <label>Discount %</label>
          <input
            type="number"
            className="form-Input w-100"
            min="0"
            max="100"
            value={overallDiscount}
            onChange={handleOverallDiscount}
          />
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center pb-2">
        <h6 className="mb-0">Pending Requisitions — select lines to include</h6>
        <div className="d-flex gap-2 align-items-center">
          <span className="text-muted small">{selectedRows.length} selected</span>
          <button type="button" className="mainbtnGry btn-sm" onClick={handleSelectAll} disabled={!rows.length || loading}>
            Select All
          </button>
          <button type="button" className="mainbtnGry btn-sm" onClick={handleDeselectAll} disabled={!selectedRows.length}>
            Clear selection
          </button>
          <button type="button" className="mainbtnGry btn-sm" onClick={handleRefresh} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      {loading ? (
        <div className="center p-4">
          <ClipLoader color="#5B93FF" />
        </div>
      ) : (
        <div className="gridshad">
          <div className="ag-theme-quartz pending-req-ag-grid" style={{ height: 320, width: '100%' }}>
            {/* <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              pagination={false}
              onSelectionChanged={onSelectionChanged}
              /> */}
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              pagination={false}
              onRowSelected={onRowSelected}
              getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
            />
          </div>
        </div>
      )}
      <div className="d-flex justify-content-between boxs mt-3 flex-wrap gap-2">
        <div className="d-flex gap-2">
          <div className="totalhead center">Total Amount</div>
          <div className="field">{parseFloat(calculatedTotal)?.toFixed(currencyPrecision) || '---'}</div>
        </div>
        <div>
          <div className="totalhead center">_</div>
        </div>
        <div className="d-flex gap-2">
          <div className="totalhead center">Total Discount</div>
          <div className="field">{parseFloat(discountAmount)?.toFixed(currencyPrecision) || '---'}</div>
        </div>
        <div>
          <div className="totalhead center">=</div>
        </div>
        <div className="d-flex gap-2">
          <div className="totalhead center">After Discount</div>
          <div className="field">{(calculatedTotal - discountAmount)?.toFixed(currencyPrecision) || '---'}</div>
        </div>
        <div>
          <div className="totalhead center">+</div>
        </div>
        <div className="d-flex gap-2">
          <div className="totalhead center">Total Vat</div>
          <div className="field">{parseFloat(vatAmount || 0)?.toFixed(currencyPrecision) || '---'}</div>
        </div>
        <div>
          <div className="totalhead center">=</div>
        </div>
        <div className="d-flex gap-2">
          <div className="totalhead center">Net Amount</div>
          <div className="field">{parseFloat(grandTotal)?.toFixed(currencyPrecision) || '---'}</div>
        </div>
      </div>
    </div>
  );
};

export default PendingRequisitionsGrid;
